import { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE } from '../config/api'

interface TeamMember {
  id: string
  name?: string
  role?: string
  avatar?: string
  bio?: string
  experience?: string
  expertise?: string[]
  github?: string
  linkedin?: string
  email?: string
  isCoreTeamMember?: boolean
}

// Helper function to check if avatar is an image URL
const isImageUrl = (avatar: unknown): avatar is string => {
  if (typeof avatar !== 'string') return false
  const str = avatar.trim()
  if (!str) return false
  return str.startsWith('http') || str.startsWith('/')
}

function getInitials(name?: string): string {
  if (!name) return '??'
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

export default function Team() {
  const ref = useRef<HTMLDivElement>(null)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const normalizeTeamMember = (raw: unknown, index: number): TeamMember => {
    const obj = (raw ?? {}) as Record<string, unknown>

    const expertise = Array.isArray(obj.expertise)
      ? obj.expertise.filter((s): s is string => typeof s === 'string')
      : []

    const id = typeof obj.id === 'string' && obj.id ? obj.id : `team-${index}`
    const name = typeof obj.name === 'string' ? obj.name : undefined
    const role = typeof obj.role === 'string' ? obj.role : undefined
    const avatar = typeof obj.avatar === 'string' && obj.avatar ? obj.avatar : '👤'
    const bio = typeof obj.bio === 'string' ? obj.bio : ''
    const experience = typeof obj.experience === 'string' ? obj.experience : ''

    const socialLinks = (obj.socialLinks ?? {}) as Record<string, unknown>
    const github = typeof obj.github === 'string' ? obj.github : (typeof socialLinks.github === 'string' ? socialLinks.github : '')
    const linkedin = typeof obj.linkedin === 'string' ? obj.linkedin : (typeof socialLinks.linkedin === 'string' ? socialLinks.linkedin : '')
    const email = typeof obj.email === 'string' ? obj.email : ''

    const isCoreTeamMember = typeof obj.isCoreTeamMember === 'boolean'
      ? obj.isCoreTeamMember
      : obj.isCoreTeamMember === undefined ? true : (obj.isCoreTeamMember === 'true' || obj.isCoreTeamMember === 1 || obj.isCoreTeamMember === '1')

    return { id, name, role, avatar, bio, experience, expertise, github, linkedin, email, isCoreTeamMember }
  }

  // Fetch team members from API
  useEffect(() => {
    const fetchTeamMembers = async () => {
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/api/v1/team`)
        if (!response.ok) {
          throw new Error(`Team API request failed: ${response.status} ${response.statusText}`)
        }
        const data = await response.json()
        const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : [])
        setTeamMembers(list.map((m: unknown, i: number) => normalizeTeamMember(m, i)))

        if (!Array.isArray(list) || (data?.success === false && data?.error)) {
          setError(typeof data?.error === 'string' ? data.error : 'Unexpected API response')
        }
      } catch (error) {
        console.error('Failed to fetch team members:', error)
        setTeamMembers([])
        setError(error instanceof Error ? error.message : 'Failed to fetch team members')
      } finally {
        setIsLoading(false)
      }
    }
    fetchTeamMembers()
  }, [])

  const coreMembers = teamMembers.filter(m => m.isCoreTeamMember !== false)
  const employees = teamMembers.filter(m => m.isCoreTeamMember === false)

  if (isLoading) {
    return (
      <section id="team" className="section-spacing">
        <div className="container-page">
          <div className="section-head">
            <span className="eyebrow">Our Team</span>
            <h2 className="mt-4" style={{ color: 'var(--text)' }}>Meet the Experts</h2>
          </div>
          <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="shimmer-card text-center flex flex-col items-center justify-center" style={{ padding: '2rem', height: '240px' }}>
                <div className="w-[88px] h-[88px] rounded-full shimmer-item mb-4" />
                <div className="h-4 w-3/4 rounded shimmer-item mb-2" />
                <div className="h-3 w-1/2 rounded shimmer-item" />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="team" ref={ref} className="section-spacing">
      <div className="container-page">
        {/* Section Header */}
        <div className="section-head">
          <span className="eyebrow">Our Team</span>
          <h2 className="mt-4" style={{ color: 'var(--text)' }}>Meet the Experts</h2>
          <p className="mt-4 text-[1.06rem]" style={{ color: 'var(--muted)' }}>
            Our diverse team of passionate developers and designers work together to create
            exceptional digital experiences.
          </p>
        </div>

        {/* Error state */}
        {error && (
          <div className="text-center py-8">
            <p style={{ color: 'var(--muted)' }}>{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!error && teamMembers.length === 0 && (
          <div className="text-center py-8">
            <p style={{ color: 'var(--muted)' }}>No team members found.</p>
          </div>
        )}

        {/* Core Team Grid */}
        <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
          {coreMembers.map((member) => (
            <Link
              key={member.id}
              to={`/team/${member.id}`}
              className="card-studio text-center no-underline"
              style={{ padding: '2rem' }}
            >
              {/* Avatar */}
              <div className="avatar-initials mb-4">
                {isImageUrl(member.avatar) ? (
                  <img
                    src={member.avatar}
                    alt={member.name ?? 'Team member'}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      target.parentElement!.textContent = getInitials(member.name)
                    }}
                  />
                ) : (
                  getInitials(member.name)
                )}
              </div>

              <h3 className="font-display text-[1.06rem] font-semibold" style={{ color: 'var(--text)' }}>
                {member.name ?? 'Unnamed'}
              </h3>
              <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                {member.role ?? ''}
              </p>

              {/* Social links */}
              <div className="flex justify-center gap-4 mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                {member.linkedin && (
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm no-underline transition-colors duration-300"
                    style={{ color: 'var(--muted)' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}
                  >
                    LinkedIn
                  </a>
                )}
                {member.github && (
                  <a
                    href={member.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm no-underline transition-colors duration-300"
                    style={{ color: 'var(--muted)' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.color = 'var(--muted)' }}
                  >
                    GitHub
                  </a>
                )}
              </div>
            </Link>
          ))}
        </div>

        {/* Extended Team / Employees Section */}
        {employees.length > 0 && (
          <div className="mt-20">
            {/* Section Header */}
            <div className="section-head mb-10">
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Extended Team & Staff</h2>
              <p className="mt-4 text-[1.06rem]" style={{ color: 'var(--muted)' }}>
                Our talented associate developers, designers, and specialists who support our projects and extend our capabilities.
              </p>
            </div>
            
            <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              {employees.map((member) => (
                <Link
                  key={member.id}
                  to={`/team/${member.id}`}
                  className="card-studio text-center no-underline"
                  style={{ padding: '2rem' }}
                >
                  {/* Avatar */}
                  <div className="avatar-initials mb-4">
                    {isImageUrl(member.avatar) ? (
                      <img
                        src={member.avatar}
                        alt={member.name ?? 'Team member'}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.parentElement!.textContent = getInitials(member.name)
                        }}
                      />
                    ) : (
                      getInitials(member.name)
                    )}
                  </div>

                  <h3 className="font-display text-[1.06rem] font-semibold" style={{ color: 'var(--text)' }}>
                    {member.name ?? 'Unnamed'}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
                    {member.role ?? ''}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Join CTA */}
        <div className="text-center mt-16">
          <div className="cta-block" style={{ maxWidth: '640px', margin: '0 auto', padding: '48px 32px' }}>
            <h3 className="font-display text-2xl font-semibold" style={{ color: 'var(--inverse-text)' }}>
              Want to Join Our Team?
            </h3>
            <p className="mt-3 text-sm" style={{ color: 'var(--inverse-text)', opacity: 0.6 }}>
              We're always looking for talented individuals passionate about creating amazing digital experiences.
            </p>
            <div className="mt-6">
              <Link to="/contact" className="btn-pill btn-inverse no-underline">
                Get In Touch
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
